// File: src/components/PageBuilder.jsx
import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import grapesjs from 'grapesjs';
import 'grapesjs/dist/css/grapes.min.css';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PageBuilder = forwardRef(({ pageId = 'adani-solar' }, ref) => {
  const editorRef = useRef(null);
  const [editorReady, setEditorReady] = useState(false);
  const [customCss, setCustomCss] = useState('');

  useImperativeHandle(ref, () => ({
    savePage: async () => {
      if (!editorRef.current) return { ok: false };

      const html = editorRef.current.getHtml();
      const css = editorRef.current.getCss();
      const combinedCss = css + '\n' + customCss;
      const finalHtml = `<style>${combinedCss}</style>\n${html}`;

      try {
        const res = await fetch(`https://api.solarstation.in/api/pages/pages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            slug: pageId,
            title: pageId,
            html: finalHtml,
            css: combinedCss,
          }),
        });

        if (!res.ok) throw new Error(`Failed to save: ${res.status}`);
        toast.success('Page saved successfully ✅');
        return await res.json();
      } catch (err) {
        toast.error(`Error saving page: ${err.message}`);
        return { ok: false };
      }
    },
  }));

  useEffect(() => {
    if (editorRef.current) return;

    const editor = grapesjs.init({
      container: '#gjs',
      height: '100%',
      width: '100%',
      fromElement: false,
      storageManager: false,
      blockManager: { appendTo: '#blocks' },
      styleManager: { appendTo: '#style-manager' },
      layerManager: { appendTo: '#layers' },
      selectorManager: { appendTo: '#selectors' },
      panels: { defaults: [] },
      canvas: {
        styles: [
          `
            body {
              margin: 0 !important;
              padding: 0 !important;
              background: #fff !important;
              display: flex;
              justify-content: center;
            }
            #wrapper {
              max-width: 100%;
            }
          `
        ]
      }
    });

    // Remove default gray background from GrapesJS outer canvas
    editor.Canvas.getCanvasView().el.style.background = '#fff';

    const selectorContainer = document.getElementById('selectors');
    if (selectorContainer) selectorContainer.style.display = 'none';

    editor.on('component:selected', () => {
      if (selectorContainer) selectorContainer.style.display = 'block';
    });

    editor.on('component:deselected', () => {
      if (selectorContainer) selectorContainer.style.display = 'none';
    });

    const pn = editor.Panels;
    pn.addPanel({ id: 'panel-top', el: '.panel__top' });
    pn.addButton('panel-top', [
      { id: 'undo', className: 'fa fa-undo', command: 'core:undo', attributes: { title: 'Undo' } },
      { id: 'redo', className: 'fa fa-repeat', command: 'core:redo', attributes: { title: 'Redo' } },
      { id: 'preview', className: 'fa fa-eye', command: 'preview', attributes: { title: 'Preview' } },
      { id: 'export', className: 'fa fa-code', command: 'export-template', attributes: { title: 'View HTML' } },
    ]);

    editor.Commands.add('export-template', {
      run(editor) {
        const html = editor.getHtml();
        const css = editor.getCss() + '\n' + customCss;
        const content = `<style>${css}</style>\n${html}`;
        const win = window.open('', '_blank');
        win.document.open();
        win.document.write(content);
        win.document.close();
      },
    });

        const bm = editor.BlockManager;

    // Basic Section
    bm.add('section', {
      label: 'Section',
      category: 'Basic',
      content: `<section style="padding:40px;text-align:center;">
        <h2>Section title</h2>
        <p>Section content</p>
      </section>`,
    });

    bm.add('two-columns', {
      label: '2 Columns',
      category: 'Layout',
      content: `<div style="display:flex;">
        <div style="flex:1;padding:10px;">Column 1</div>
        <div style="flex:1;padding:10px;">Column 2</div>
      </div>`,
    });


    // Basic Heading
    bm.add('heading', {
      label: 'Heading',
      category: 'Basic',
      content: `<h1 style="font-size:2em;font-weight:bold;">Heading Text</h1>`,
    });

    // Basic Paragraph
    bm.add('paragraph', {
      label: 'Paragraph',
      category: 'Basic',
      content: `<p style="font-size:1em;line-height:1.5;">This is a sample paragraph. You can edit this text.</p>`,
    });

    // Basic Image
    bm.add('image', {
      label: 'Image',
      category: 'Basic',
      content: `<img src="https://via.placeholder.com/300" alt="Image" style="max-width:100%;" />`,
    });

    // Basic Button
    bm.add('button', {
      label: 'Button',
      category: 'Basic',
      content: `<button style="padding:10px 20px;background:#4cafef;color:#fff;border:none;border-radius:4px;">Click Me</button>`,
    });

    // Divider
    bm.add('divider', {
      label: 'Divider',
      category: 'Basic',
      content: `<hr style="border:none;border-top:1px solid #ccc;margin:20px 0;" />`,
    });

    // List
    bm.add('list', {
      label: 'List',
      category: 'Basic',
      content: `<ul style="padding-left:20px;">
        <li>List Item 1</li>
        <li>List Item 2</li>
        <li>List Item 3</li>
      </ul>`,
    });


    // Editable Text
    bm.add('editable-text', {
      label: 'Editable Text',
      category: 'Basic',
      content: `<div data-gjs-type="text" style="font-size:1.2em;line-height:1.5;">
        Double-click to edit this text
      </div>`,
    });

    // Accordion
bm.add('accordion', {
  label: 'Accordion',
  category: 'Components',
  content: `<div>
    <details>
      <summary>Accordion Title 1</summary>
      <p>Accordion content goes here...</p>
    </details>
    <details>
      <summary>Accordion Title 2</summary>
      <p>Accordion content goes here...</p>
    </details>
  </div>`,
});

// Tabs
bm.add('tabs', {
  label: 'Tabs',
  category: 'Components',
  content: `<div>
    <nav style="display:flex;gap:10px;">
      <button style="padding:8px 12px;">Tab 1</button>
      <button style="padding:8px 12px;">Tab 2</button>
    </nav>
    <div style="padding:10px;border:1px solid #ccc;margin-top:5px;">
      Tab content goes here...
    </div>
  </div>`,
});



    bm.add('hero', {
      label: 'Hero',
      category: 'Sections',
      content: `<section style="padding:80px 20px;background:linear-gradient(90deg,#667eea,#764ba2);color:#fff;text-align:center;">
        <h1>Hero Title</h1>
        <p>Hero subtitle</p>
        <a href="#" style="display:inline-block;margin-top:18px;padding:10px 22px;background:white;color:#6b46c1;border-radius:4px;text-decoration:none;">Get Started</a>
      </section>`,
    });

    bm.add('link', {
      label: 'Link',
      category: 'Basic',
      content: `<a href="#" style="color:blue;text-decoration:underline;">Click here</a>`,
    });

    bm.add('card', {
      label: 'Card',
      category: 'Components',
      content: `<div style="border:1px solid #eee;padding:16px;border-radius:8px;">
        <h4>Card Title</h4>
        <p>Card content here</p>
      </div>`,
    });

    bm.add('contact-form', {
      label: 'Contact Form',
      category: 'Forms',
      content: `<form>
        <input type="text" placeholder="Name" style="display:block;margin-bottom:10px;width:100%;" />
        <input type="email" placeholder="Email" style="display:block;margin-bottom:10px;width:100%;" />
        <textarea placeholder="Message" style="display:block;margin-bottom:10px;width:100%;"></textarea>
        <button type="submit" style="padding:10px 20px;background:#2d9cdb;color:#fff;border-radius:4px;">Send</button>
      </form>`,
    });

    // ✅ New Useful Blocks

    // Navbar
    bm.add('navbar', {
      label: 'Navbar',
      category: 'Navigation',
      content: `<nav style="display:flex;justify-content:space-between;align-items:center;padding:10px 20px;background:#333;color:#fff;">
        <div style="font-weight:bold;">MyLogo</div>
        <div>
          <a href="#" style="color:#fff;margin:0 10px;text-decoration:none;">Home</a>
          <a href="#" style="color:#fff;margin:0 10px;text-decoration:none;">About</a>
          <a href="#" style="color:#fff;margin:0 10px;text-decoration:none;">Contact</a>
        </div>
      </nav>`,
    });

    // Footer
    bm.add('footer', {
      label: 'Footer',
      category: 'Sections',
      content: `<footer style="background:#222;color:#fff;text-align:center;padding:20px;">
        <p>&copy; 2025 My Website. All rights reserved.</p>
      </footer>`,
    });

    // Image Gallery
    bm.add('image-gallery', {
      label: 'Image Gallery',
      category: 'Media',
      content: `<div style="display:grid;grid-template-columns:repeat(3, 1fr);gap:10px;">
        <img src="https://via.placeholder.com/150" style="width:100%;" />
        <img src="https://via.placeholder.com/150" style="width:100%;" />
        <img src="https://via.placeholder.com/150" style="width:100%;" />
      </div>`,
    });

    // Image with Caption
bm.add('image-caption', {
  label: 'Image with Caption',
  category: 'Media',
  content: `<figure style="text-align:center;">
    <img src="https://via.placeholder.com/400" style="max-width:100%;" />
    <figcaption data-gjs-type="text" style="font-size:0.9em;color:#555;">Image caption</figcaption>
  </figure>`,
});


    // Testimonial
    bm.add('testimonial', {
      label: 'Testimonial',
      category: 'Components',
      content: `<blockquote style="padding:20px;border-left:5px solid #ccc;background:#f9f9f9;">
        <p>"This is the best service ever! Highly recommend."</p>
        <footer>- John Doe</footer>
      </blockquote>`,
    });

    // Video Embed
    // bm.add('video-embed', {
    //   label: 'Video Embed',
    //   category: 'Media',
    //   content: `<div style="position:relative;padding-bottom:56.25%;height:0;">
    //     <iframe src="https://www.youtube.com/embed/dQw4w9WgXcQ" 
    //     style="position:absolute;top:0;left:0;width:100%;height:100%;" frameborder="0" allowfullscreen></iframe>
    //   </div>`,
    // });

    // Pricing Table
    bm.add('pricing-table', {
      label: 'Pricing Table',
      category: 'Components',
      content: `<div style="display:flex;gap:20px;">
        <div style="flex:1;border:1px solid #ddd;padding:20px;text-align:center;">
          <h3>Basic</h3>
          <p style="font-size:24px;">$9/month</p>
          <ul style="list-style:none;padding:0;">
            <li>Feature 1</li>
            <li>Feature 2</li>
          </ul>
          <button style="padding:10px 20px;background:#4cafef;color:#fff;border:none;border-radius:4px;">Choose</button>
        </div>
        <div style="flex:1;border:1px solid #ddd;padding:20px;text-align:center;">
          <h3>Premium</h3>
          <p style="font-size:24px;">$29/month</p>
          <ul style="list-style:none;padding:0;">
            <li>All Basic features</li>
            <li>Extra Feature</li>
          </ul>
          <button style="padding:10px 20px;background:#4cafef;color:#fff;border:none;border-radius:4px;">Choose</button>
        </div>
      </div>`,
    });

    // FAQ Section
    bm.add('faq', {
      label: 'FAQ',
      category: 'Sections',
      content: `<section style="padding:40px;">
        <h2>Frequently Asked Questions</h2>
        <details>
          <summary>What is your return policy?</summary>
          <p>We offer a 30-day return policy...</p>
        </details>
        <details>
          <summary>Do you ship internationally?</summary>
          <p>Yes, we ship to over 50 countries worldwide.</p>
        </details>
      </section>`,
    });


    fetch(`https://api.solarstation.in/api/pages/pages/${encodeURIComponent(pageId)}`)
      .then((r) => {
        if (!r.ok) throw new Error(`Failed to load page: ${r.status}`);
        return r.json();
      })
      .then((data) => {
        setCustomCss(data.css || '');
        if (data && data.html) {
          editor.setComponents(`<style>${data.css || ''}</style>\n${data.html}`);
        } else {
          editor.setComponents('<h1>Welcome to your builder</h1>');
        }
        setEditorReady(true);
      })
      .catch((err) => {
        toast.error(`Error loading page: ${err.message}`);
        setEditorReady(true);
      });

    editorRef.current = editor;

    return () => {
      editor.destroy();
      editorRef.current = null;
    };
  }, [pageId]);

  return (
    <div style={{ display: 'flex', height: '100vh', width: '100vw', overflow: 'hidden' }}>
      {/* Left Panel */}
      <div style={{
        width: 280,
        flexShrink: 0,
        borderRight: '1px solid #e0e0e0',
        padding: 12,
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        overflowY: 'auto'
      }}>
        <div className="panel__top" style={{ marginBottom: 4 }} />
        <div id="blocks" style={{ flex: 1, border: '1px solid #ddd', borderRadius: 8, padding: 10, overflow: 'auto', background: '#fafafa' }} />
        <div id="style-manager" style={{ height: '30%', border: '1px solid #ddd', borderRadius: 8, padding: 10, overflow: 'auto', background: '#fafafa' }} />
        <div>
          <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: 6 }}>🎨 Custom CSS</h4>
          <textarea value={customCss} onChange={(e) => setCustomCss(e.target.value)}
            style={{ width: '100%', height: '100px', padding: '8px', borderRadius: '6px', border: '1px solid #ccc', fontFamily: 'monospace', fontSize: '13px' }}
          />
        </div>
      </div>

      {/* Middle Editor */}
      <div id="gjs" style={{ flex: 1, minWidth: 0, backgroundColor: '#fff', overflow: 'auto' }} />

      {/* Right Panel */}
      <div style={{
        width: 260,
        flexShrink: 0,
        borderLeft: '1px solid #e0e0e0',
        padding: 12,
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        overflowY: 'auto'
      }}>
        <div>
          <h4 style={{ fontSize: '14px', fontWeight: '900', marginBottom: 6 }}>📂 Layers</h4>
          <div id="layers" style={{ height: '35vh', border: '1px solid #ddd', borderRadius: 8, padding: 8, overflow: 'auto', background: '#fafafaff' }} />
        </div>
        <div>
          <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: 6 }}>🎯 Selectors</h4>
          <div id="selectors" style={{ height: '35vh', border: '1px solid #ddd', borderRadius: 8, padding: 8, overflow: 'auto', background: '#fafafa' }} />
        </div>
      </div>
    </div>
  );
});

export default PageBuilder;
